import React, { forwardRef, useState, useEffect } from 'react';
import { Input } from '@heroui/react';
import cn from 'classnames';
import { Svg } from '@/shared/ui/Svg';
import UploadCloudIcon from '@/shared/assets/icons/complex/upload-cloud.svg';
import styles from './CustomFileInput.module.scss';

export interface CustomFileInputProps {
  className?: string;
  value: File | null | string;
  onChange: (file: File | null) => void;
  accept?: string;
  initialPreviewPicture?: string | null;
  errorMessage?: string;
  showError?: boolean;
  roundVersion?: boolean;
}

export const CustomFileInput = forwardRef<HTMLInputElement, CustomFileInputProps>(
  (
    {
      value,
      onChange,
      accept = 'image/*',
      className,
      initialPreviewPicture,
      errorMessage,
      showError = false,
      roundVersion = false,
      ...rest
    },
    ref,
  ) => {
    const previewFile = initialPreviewPicture ? `${initialPreviewPicture}` : null;

    const [preview, setPreview] = useState<string | null>(previewFile);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      onChange(file);
    };

    const error = !!errorMessage;
    const classnamesFileInput = {
      base: roundVersion ? styles.fileInputBaseRound : styles.fileInputBase,
      inputWrapper: cn(styles.fileInputWrapper),
      innerWrapper: preview
        ? cn(
            styles.fileInputInnerWrapper,
            roundVersion && styles.fileInputInnerWrapperRound,
            styles.selected,
          )
        : cn(
            styles.fileInputInnerWrapper,
            roundVersion && styles.fileInputInnerWrapperRound,
            error && styles.error,
          ),
    };

    useEffect(() => {
      if (value instanceof File) {
        const reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result as string);
        reader.readAsDataURL(value);
      } else if (initialPreviewPicture) {
        setPreview(previewFile);
      } else {
        setPreview(null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, initialPreviewPicture, previewFile]);

    return (
      <div>
        <Input
          {...rest}
          ref={ref}
          type="file"
          accept={accept}
          onChange={handleChange}
          isInvalid={error}
          startContent={
            preview || initialPreviewPicture ? (
              <div className="absolute inset-0">
                <img
                  src={preview || initialPreviewPicture || ''}
                  alt="Preview"
                  className={cn(
                    'w-full h-full object-cover',
                    roundVersion ? 'rounded-full' : 'rounded-xl',
                  )}
                />
              </div>
            ) : (
              <>
                <Svg
                  Icon={UploadCloudIcon}
                  className={cn(styles.uploadIcon, roundVersion && styles.uploadIconRound)}
                  stroke="white"
                  width={roundVersion ? 24 : 40}
                  height={roundVersion ? 24 : 40}
                />
                {!roundVersion && (
                  <div className="flex flex-col items-center justify-center gap-1">
                    <span className={styles.fileInputSpan}>Click to upload or drag & drop</span>
                    <span className={styles.fileInputSpanDescription}>
                      PNG, JPG (max 1024×1024px)
                    </span>
                  </div>
                )}
                {roundVersion && (
                  <div className="flex flex-col items-center justify-center gap-1">
                    <span className={styles.fileInputSpanRound}>PNG, JPG</span>
                    <span className={styles.fileInputSpanDescriptionRound}>
                      (max 1024×1024px)
                    </span>
                  </div>
                )}
              </>
            )
          }
          classNames={classnamesFileInput}
          className={className}
        />
        {showError && errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
      </div>
    );
  },
);

export default CustomFileInput;
